import { Jwt } from "@/utils/jwt.util";
import { create } from 'zustand';
import { User } from './user.model';
import { persist, createJSONStorage } from 'zustand/middleware'
import { WalletStore } from "../wallet/wallet.store";
import { SettingsStore, getDefaultLanguage } from "../settings/settings.store";

const jwt = new Jwt();

export type SignInProps = {
  data: User;
  accessToken: string;
  refreshToken: string;
}
interface AuthState {
  email?: string;
  loading: boolean;
  authenticatedUser?: string;
}

interface AuthActions {
  setEmail: (email: string) => void;
  setLoading: (loading: boolean) => void;
  signIn: ({
    data,
    accessToken,
    refreshToken,
  }: SignInProps) => Promise<void>;
  getSession: () => Promise<User | null>;
  getUser: () => Promise<User | null>;
  signOut: () => void;
  getAuthAccessToken: () => Promise<string | null>;
  getAuthRefreshToken: () => Promise<string | null>;
  updateUser: (user: User) => Promise<void>;
}

export const AuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      email: '',
      loading: false,
      setEmail: email => set({ email }),
      setLoading: loading => set({ loading }),
      signIn: async ({ data, accessToken, refreshToken }) => {
        const authenticatedUser = await jwt.sign({
          payload: {
            data,
            accessToken,
            refreshToken,
          },
        });
        set({ email: '' });
        set({ authenticatedUser });
      },
      getAuthAccessToken: async () => {
        const { authenticatedUser } = get();
        if (!authenticatedUser) return null;

        const { accessToken } = (await jwt.verify({ jwt: authenticatedUser })) as {
          accessToken: string;
        };

        return accessToken;
      },

      getAuthRefreshToken: async () => {
        const { authenticatedUser } = get();
        if (!authenticatedUser) return null;

        const { refreshToken } = (await jwt.verify({ jwt: authenticatedUser })) as {
          refreshToken: string;
        };

        return refreshToken;
      },
      getSession: async () => {
        const { authenticatedUser } = get();
        if (!authenticatedUser) {
          SettingsStore.getState().setLanguage(getDefaultLanguage());
          return null;
        }

        const data = (await jwt.verify({ jwt: authenticatedUser }));
        return data as User;
      },
      getUser: async () => {
        const { authenticatedUser } = get();
        if (!authenticatedUser) return null;

        const { data } = (await jwt.verify({ jwt: authenticatedUser }));
        return data as User;
      },
      signOut: () => {
        WalletStore.getState().reset();
        SettingsStore.getState().setLanguage(getDefaultLanguage());
        set({ email: '', authenticatedUser: undefined });
      },
      updateUser: async (user) => {
        const { authenticatedUser } = get();
        if (!authenticatedUser) return;

        const { refreshToken, accessToken } = (await jwt.verify({ jwt: authenticatedUser })) as {
          refreshToken: string;
          accessToken: string;
        };

        const updatedUser = await jwt.sign({
          payload: {
            data: user,
            accessToken,
            refreshToken,
          },
        });

        set({ authenticatedUser: updatedUser });
      },
    }),
    {
      name: "_auth_wallet_storage",
      storage: createJSONStorage(() => localStorage),
    }
  )
)